/**
 * Strategy Kernel Store
 *
 * Zustand store for the Rumelt Kernel Strategy module.
 * DB-first: Supabase is the system of record for all five tables.
 *
 * Tables managed:
 *   strategy_kernels
 *   strategy_challenges
 *   strategy_proximate_objectives
 *   strategy_takes
 *   strategy_assumptions
 */

import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import { useUserStore } from './userStore';
import type {
  StrategyKernel,
  StrategyChallenge,
  ProximateObjective,
  StrategyTake,
  StrategyAssumption,
  CoherentAction,
  TakeAction,
  TakeInterdependency,
  TakeDeliverable,
  CreateKernelInput,
  UpdateKernelInput,
  CreateChallengeInput,
  UpdateChallengeInput,
  CreateProximateObjectiveInput,
  UpdateProximateObjectiveInput,
  CreateTakeInput,
  UpdateTakeInput,
  CreateAssumptionInput,
  UpdateAssumptionInput,
} from '../../types/prioritization/strategyKernel';
import { buildObjectivesTree, MAX_COHERENT_ACTIONS, MAX_TAKE_ACTIONS } from '../../types/prioritization/strategyKernel';

// ============================================================================
// State shape
// ============================================================================

interface StrategyKernelState {
  kernels: Record<string, StrategyKernel>;
  challenges: Record<string, StrategyChallenge>;
  objectives: Record<string, ProximateObjective>;
  takes: Record<string, StrategyTake>;
  assumptions: Record<string, StrategyAssumption>;

  isLoading: boolean;
  error: string | null;

  // Supabase sync
  fetchFromSupabase: () => Promise<void>;

  // Kernel CRUD
  createKernel: (input: CreateKernelInput) => Promise<string>;
  updateKernel: (id: string, updates: UpdateKernelInput) => Promise<void>;
  deleteKernel: (id: string) => Promise<void>;
  activateKernel: (id: string) => Promise<void>;
  archiveKernel: (id: string) => Promise<void>;
  addCoherentAction: (kernelId: string, action: Omit<CoherentAction, 'id' | 'order'>) => Promise<void>;
  updateCoherentAction: (kernelId: string, actionId: string, updates: Partial<CoherentAction>) => Promise<void>;
  removeCoherentAction: (kernelId: string, actionId: string) => Promise<void>;
  reorderCoherentActions: (kernelId: string, orderedIds: string[]) => Promise<void>;

  // Challenge CRUD
  createChallenge: (input: CreateChallengeInput) => Promise<string>;
  updateChallenge: (id: string, updates: UpdateChallengeInput) => Promise<void>;
  deleteChallenge: (id: string) => Promise<void>;
  setCrux: (kernelId: string, challengeId: string) => Promise<void>;
  clearCrux: (kernelId: string) => Promise<void>;

  // Proximate Objective CRUD
  createObjective: (input: CreateProximateObjectiveInput) => Promise<{ ok: boolean; id?: string; error?: string }>;
  updateObjective: (id: string, updates: UpdateProximateObjectiveInput) => Promise<void>;
  deleteObjective: (id: string) => Promise<void>;

  // Take CRUD
  createTake: (input: CreateTakeInput) => Promise<string>;
  updateTake: (id: string, updates: UpdateTakeInput) => Promise<void>;
  deleteTake: (id: string) => Promise<void>;
  addTakeAction: (takeId: string, action: Omit<TakeAction, 'id'>) => Promise<void>;
  addInterdependency: (takeId: string, dep: Omit<TakeInterdependency, 'id'>) => Promise<void>;
  addDeliverable: (takeId: string, del: Omit<TakeDeliverable, 'id'>) => Promise<void>;

  // Assumption CRUD
  createAssumption: (input: CreateAssumptionInput) => Promise<string>;
  updateAssumption: (id: string, updates: UpdateAssumptionInput) => Promise<void>;
  deleteAssumption: (id: string) => Promise<void>;

  // Selectors
  getKernel: (id: string) => StrategyKernel | undefined;
  getAllKernels: () => StrategyKernel[];
  getChallengesForKernel: (kernelId: string) => StrategyChallenge[];
  getObjectivesForKernel: (kernelId: string) => ProximateObjective[];
  getObjectivesTree: (kernelId: string) => ProximateObjective[];
  getTakesForKernel: (kernelId: string) => StrategyTake[];
  getTakesForObjective: (objectiveId: string) => StrategyTake[];
  getAssumptionsForKernel: (kernelId: string) => StrategyAssumption[];
  getOverdueAssumptions: (kernelId: string) => StrategyAssumption[];

  // Utility
  reset: () => void;
  setError: (error: string | null) => void;
}

// ============================================================================
// DB → domain mappers
// ============================================================================

function rowToKernel(row: Record<string, unknown>): StrategyKernel {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    name: row.name as string,
    description: row.description as string | undefined,
    diagnosis: (row.diagnosis as string) ?? '',
    guidingPolicy: (row.guiding_policy as string) ?? '',
    coherentActions: (row.coherent_actions as CoherentAction[]) ?? [],
    cruxId: row.crux_id as string | undefined,
    status: (row.status as StrategyKernel['status']) ?? 'draft',
    timeHorizon: (row.time_horizon as StrategyKernel['timeHorizon']) ?? 'annual',
    linkedCycleIds: (row.linked_cycle_ids as string[]) ?? [],
    ownerId: row.owner_id as string | undefined,
    createdBy: row.created_by as string,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

function rowToChallenge(row: Record<string, unknown>): StrategyChallenge {
  return {
    id: row.id as string,
    kernelId: row.kernel_id as string,
    workspaceId: row.workspace_id as string,
    title: row.title as string,
    description: row.description as string | undefined,
    importance: row.importance as number,
    addressability: row.addressability as number,
    isCrux: (row.is_crux as boolean) ?? false,
    createdBy: row.created_by as string,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

function rowToObjective(row: Record<string, unknown>): ProximateObjective {
  return {
    id: row.id as string,
    kernelId: row.kernel_id as string,
    workspaceId: row.workspace_id as string,
    parentId: row.parent_id as string | undefined,
    orgUnitId: row.org_unit_id as string | undefined,
    title: row.title as string,
    description: row.description as string | undefined,
    level: (row.level as number) ?? 0,
    status: (row.status as ProximateObjective['status']) ?? 'draft',
    dueDate: row.due_date as string | undefined,
    createdBy: row.created_by as string,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

function rowToTake(row: Record<string, unknown>): StrategyTake {
  return {
    id: row.id as string,
    kernelId: row.kernel_id as string,
    proximateObjectiveId: row.proximate_objective_id as string | undefined,
    workspaceId: row.workspace_id as string,
    orgUnitId: row.org_unit_id as string | undefined,
    committedActions: (row.committed_actions as TakeAction[]) ?? [],
    interdependencies: (row.interdependencies as TakeInterdependency[]) ?? [],
    deliverables: (row.deliverables as TakeDeliverable[]) ?? [],
    milestoneDate: row.milestone_date as string | undefined,
    createdBy: row.created_by as string,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

function rowToAssumption(row: Record<string, unknown>): StrategyAssumption {
  return {
    id: row.id as string,
    kernelId: row.kernel_id as string,
    workspaceId: row.workspace_id as string,
    assumptionText: row.assumption_text as string,
    reviewDate: row.review_date as string | undefined,
    status: (row.status as StrategyAssumption['status']) ?? 'active',
    reviewNotes: row.review_notes as string | undefined,
    createdBy: row.created_by as string,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

// ============================================================================
// Helper: current user ID (falls back to anonymous)
// ============================================================================

function getCurrentUserId(): string | null {
  try {
    const userId = useUserStore.getState().userId;
    // 'unknown' is the default placeholder the userStore uses before auth hydrates
    if (!userId || userId === 'unknown') return null;
    return userId;
  } catch {
    return null;
  }
}

// ============================================================================
// Initial state
// ============================================================================

const initialState = {
  kernels: {},
  challenges: {},
  objectives: {},
  takes: {},
  assumptions: {},
  isLoading: false,
  error: null,
};

// ============================================================================
// Store
// ============================================================================

export const useStrategyKernelStore = create<StrategyKernelState>()((set, get) => ({
  ...initialState,

  // --------------------------------------------------------------------------
  // Fetch all five tables from Supabase
  // --------------------------------------------------------------------------
  fetchFromSupabase: async () => {
    if (!isSupabaseAvailable()) {
      console.log('[StrategyKernelStore] Supabase not available');
      return;
    }

    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      set({ ...initialState });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      const [kernelsRes, challengesRes, objectivesRes, takesRes, assumptionsRes] =
        await Promise.all([
          supabase.from('strategy_kernels').select('*').eq('workspace_id', workspaceId).order('created_at', { ascending: false }),
          supabase.from('strategy_challenges').select('*').eq('workspace_id', workspaceId),
          supabase.from('strategy_proximate_objectives').select('*').eq('workspace_id', workspaceId),
          supabase.from('strategy_takes').select('*').eq('workspace_id', workspaceId),
          supabase.from('strategy_assumptions').select('*').eq('workspace_id', workspaceId),
        ]);

      if (kernelsRes.error) throw kernelsRes.error;
      if (challengesRes.error) throw challengesRes.error;
      if (objectivesRes.error) throw objectivesRes.error;
      if (takesRes.error) throw takesRes.error;
      if (assumptionsRes.error) throw assumptionsRes.error;

      const kernels: Record<string, StrategyKernel> = {};
      for (const r of kernelsRes.data ?? []) kernels[r.id] = rowToKernel(r as Record<string, unknown>);

      const challenges: Record<string, StrategyChallenge> = {};
      for (const r of challengesRes.data ?? []) challenges[r.id] = rowToChallenge(r as Record<string, unknown>);

      const objectives: Record<string, ProximateObjective> = {};
      for (const r of objectivesRes.data ?? []) objectives[r.id] = rowToObjective(r as Record<string, unknown>);

      const takes: Record<string, StrategyTake> = {};
      for (const r of takesRes.data ?? []) takes[r.id] = rowToTake(r as Record<string, unknown>);

      const assumptions: Record<string, StrategyAssumption> = {};
      for (const r of assumptionsRes.data ?? []) assumptions[r.id] = rowToAssumption(r as Record<string, unknown>);

      set({ kernels, challenges, objectives, takes, assumptions, isLoading: false, error: null });
      console.log(`[StrategyKernelStore] Loaded: ${Object.keys(kernels).length} kernels, ${Object.keys(challenges).length} challenges`);
    } catch (err) {
      console.error('[StrategyKernelStore] Fetch error:', err);
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Unknown error' });
    }
  },

  // --------------------------------------------------------------------------
  // Kernel CRUD
  // --------------------------------------------------------------------------
  createKernel: async (input) => {
    const id = uuidv4();
    const workspaceId = getCurrentWorkspaceId() ?? '';
    const userId = getCurrentUserId();
    const now = new Date();

    const kernel: StrategyKernel = {
      id,
      workspaceId,
      name: input.name,
      description: input.description,
      diagnosis: input.diagnosis ?? '',
      guidingPolicy: input.guidingPolicy ?? '',
      coherentActions: [],
      cruxId: undefined,
      status: 'draft',
      timeHorizon: input.timeHorizon ?? 'annual',
      linkedCycleIds: input.linkedCycleIds ?? [],
      ownerId: input.ownerId,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    set(state => ({ kernels: { ...state.kernels, [id]: kernel } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_kernels').insert({
          id,
          workspace_id: workspaceId,
          name: kernel.name,
          description: kernel.description,
          diagnosis: kernel.diagnosis,
          guiding_policy: kernel.guidingPolicy,
          coherent_actions: kernel.coherentActions,
          status: kernel.status,
          time_horizon: kernel.timeHorizon,
          linked_cycle_ids: kernel.linkedCycleIds,
          owner_id: kernel.ownerId,
          created_by: userId,
        });
        if (error) console.error('[StrategyKernelStore] createKernel error:', error);
      }
    }

    return id;
  },

  updateKernel: async (id, updates) => {
    const existing = get().kernels[id];
    if (!existing) return;

    const updated: StrategyKernel = {
      ...existing,
      ...(updates.name !== undefined && { name: updates.name }),
      ...(updates.description !== undefined && { description: updates.description }),
      ...(updates.diagnosis !== undefined && { diagnosis: updates.diagnosis }),
      ...(updates.guidingPolicy !== undefined && { guidingPolicy: updates.guidingPolicy }),
      ...(updates.coherentActions !== undefined && { coherentActions: updates.coherentActions }),
      ...(updates.cruxId !== undefined && { cruxId: updates.cruxId ?? undefined }),
      ...(updates.status !== undefined && { status: updates.status }),
      ...(updates.timeHorizon !== undefined && { timeHorizon: updates.timeHorizon }),
      ...(updates.linkedCycleIds !== undefined && { linkedCycleIds: updates.linkedCycleIds }),
      ...(updates.ownerId !== undefined && { ownerId: updates.ownerId }),
      updatedAt: new Date(),
    };

    set(state => ({ kernels: { ...state.kernels, [id]: updated } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const dbUpdates: Record<string, unknown> = { updated_at: updated.updatedAt };
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.diagnosis !== undefined) dbUpdates.diagnosis = updates.diagnosis;
        if (updates.guidingPolicy !== undefined) dbUpdates.guiding_policy = updates.guidingPolicy;
        if (updates.coherentActions !== undefined) dbUpdates.coherent_actions = updates.coherentActions;
        if (updates.cruxId !== undefined) dbUpdates.crux_id = updates.cruxId;
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.timeHorizon !== undefined) dbUpdates.time_horizon = updates.timeHorizon;
        if (updates.linkedCycleIds !== undefined) dbUpdates.linked_cycle_ids = updates.linkedCycleIds;
        if (updates.ownerId !== undefined) dbUpdates.owner_id = updates.ownerId;

        const { error } = await supabase.from('strategy_kernels').update(dbUpdates).eq('id', id);
        if (error) console.error('[StrategyKernelStore] updateKernel error:', error);
      }
    }
  },

  deleteKernel: async (id) => {
    set(state => {
      const { [id]: _removed, ...rest } = state.kernels;
      // Also remove child rows from local state
      const challenges = Object.fromEntries(Object.entries(state.challenges).filter(([, c]) => c.kernelId !== id));
      const objectives = Object.fromEntries(Object.entries(state.objectives).filter(([, o]) => o.kernelId !== id));
      const takes = Object.fromEntries(Object.entries(state.takes).filter(([, t]) => t.kernelId !== id));
      const assumptions = Object.fromEntries(Object.entries(state.assumptions).filter(([, a]) => a.kernelId !== id));
      return { kernels: rest, challenges, objectives, takes, assumptions };
    });

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_kernels').delete().eq('id', id);
        if (error) console.error('[StrategyKernelStore] deleteKernel error:', error);
      }
    }
  },

  activateKernel: async (id) => {
    await get().updateKernel(id, { status: 'active' });
  },

  archiveKernel: async (id) => {
    await get().updateKernel(id, { status: 'archived' });
  },

  addCoherentAction: async (kernelId, action) => {
    const kernel = get().kernels[kernelId];
    if (!kernel) return;

    if (kernel.coherentActions.length >= MAX_COHERENT_ACTIONS) {
      throw new Error(`Cannot add more than ${MAX_COHERENT_ACTIONS} coherent actions per strategy.`);
    }

    const newAction: CoherentAction = {
      id: uuidv4(),
      title: action.title,
      description: action.description,
      ownerId: action.ownerId,
      order: kernel.coherentActions.length,
    };

    const coherentActions = [...kernel.coherentActions, newAction];
    await get().updateKernel(kernelId, { coherentActions });
  },

  updateCoherentAction: async (kernelId, actionId, updates) => {
    const kernel = get().kernels[kernelId];
    if (!kernel) return;

    const coherentActions = kernel.coherentActions.map(a =>
      a.id === actionId ? { ...a, ...updates } : a
    );
    await get().updateKernel(kernelId, { coherentActions });
  },

  removeCoherentAction: async (kernelId, actionId) => {
    const kernel = get().kernels[kernelId];
    if (!kernel) return;

    const coherentActions = kernel.coherentActions
      .filter(a => a.id !== actionId)
      .map((a, i) => ({ ...a, order: i }));
    await get().updateKernel(kernelId, { coherentActions });
  },

  reorderCoherentActions: async (kernelId, orderedIds) => {
    const kernel = get().kernels[kernelId];
    if (!kernel) return;

    const map = new Map(kernel.coherentActions.map(a => [a.id, a]));
    const coherentActions = orderedIds
      .filter(id => map.has(id))
      .map((id, i) => ({ ...map.get(id)!, order: i }));
    await get().updateKernel(kernelId, { coherentActions });
  },

  // --------------------------------------------------------------------------
  // Challenge CRUD
  // --------------------------------------------------------------------------
  createChallenge: async (input) => {
    const id = uuidv4();
    const workspaceId = getCurrentWorkspaceId() ?? '';
    const userId = getCurrentUserId();
    const now = new Date();

    const challenge: StrategyChallenge = {
      id,
      kernelId: input.kernelId,
      workspaceId,
      title: input.title,
      description: input.description,
      importance: input.importance ?? 5,
      addressability: input.addressability ?? 5,
      isCrux: false,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    set(state => ({ challenges: { ...state.challenges, [id]: challenge } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_challenges').insert({
          id,
          kernel_id: challenge.kernelId,
          workspace_id: workspaceId,
          title: challenge.title,
          description: challenge.description,
          importance: challenge.importance,
          addressability: challenge.addressability,
          is_crux: false,
          created_by: userId,
        });
        if (error) console.error('[StrategyKernelStore] createChallenge error:', error);
      }
    }

    return id;
  },

  updateChallenge: async (id, updates) => {
    const existing = get().challenges[id];
    if (!existing) return;

    const updated = { ...existing, ...updates, updatedAt: new Date() };
    set(state => ({ challenges: { ...state.challenges, [id]: updated } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase
          .from('strategy_challenges')
          .update({ ...updates, updated_at: updated.updatedAt })
          .eq('id', id);
        if (error) console.error('[StrategyKernelStore] updateChallenge error:', error);
      }
    }
  },

  deleteChallenge: async (id) => {
    const challenge = get().challenges[id];
    set(state => {
      const { [id]: _removed, ...rest } = state.challenges;
      return { challenges: rest };
    });

    // If this was the crux, clear it on the kernel
    if (challenge?.isCrux) {
      await get().updateKernel(challenge.kernelId, { cruxId: null });
    }

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_challenges').delete().eq('id', id);
        if (error) console.error('[StrategyKernelStore] deleteChallenge error:', error);
      }
    }
  },

  setCrux: async (kernelId, challengeId) => {
    const { challenges } = get();

    // Clear is_crux on all challenges for this kernel
    const updatedChallenges = { ...challenges };
    for (const [cid, c] of Object.entries(updatedChallenges)) {
      if (c.kernelId === kernelId) {
        updatedChallenges[cid] = { ...c, isCrux: cid === challengeId };
      }
    }
    set({ challenges: updatedChallenges });

    // Update kernel
    await get().updateKernel(kernelId, { cruxId: challengeId });

    // Sync to DB
    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        // Clear all cruxes for this kernel then set the new one
        await supabase
          .from('strategy_challenges')
          .update({ is_crux: false })
          .eq('kernel_id', kernelId);
        await supabase
          .from('strategy_challenges')
          .update({ is_crux: true })
          .eq('id', challengeId);
      }
    }
  },

  clearCrux: async (kernelId) => {
    const { challenges } = get();
    const updatedChallenges = { ...challenges };
    for (const [cid, c] of Object.entries(updatedChallenges)) {
      if (c.kernelId === kernelId && c.isCrux) {
        updatedChallenges[cid] = { ...c, isCrux: false };
      }
    }
    set({ challenges: updatedChallenges });
    await get().updateKernel(kernelId, { cruxId: null });

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        await supabase
          .from('strategy_challenges')
          .update({ is_crux: false })
          .eq('kernel_id', kernelId);
      }
    }
  },

  // --------------------------------------------------------------------------
  // Proximate Objective CRUD
  // --------------------------------------------------------------------------
  createObjective: async (input) => {
    const id = uuidv4();
    const workspaceId = getCurrentWorkspaceId() ?? '';
    const userId = getCurrentUserId();
    const now = new Date();

    const objective: ProximateObjective = {
      id,
      kernelId: input.kernelId,
      workspaceId,
      parentId: input.parentId,
      orgUnitId: input.orgUnitId,
      title: input.title,
      description: input.description,
      level: input.level ?? 0,
      status: 'draft',
      dueDate: input.dueDate,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    set(state => ({ objectives: { ...state.objectives, [id]: objective } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_proximate_objectives').insert({
          id,
          kernel_id: objective.kernelId,
          workspace_id: workspaceId,
          parent_id: objective.parentId,
          org_unit_id: objective.orgUnitId,
          title: objective.title,
          description: objective.description,
          level: objective.level,
          status: objective.status,
          due_date: objective.dueDate,
          created_by: userId,
        });
        if (error) {
          console.error('[StrategyKernelStore] createObjective error:', error);
          // Roll back the optimistic row: a save that only lives in this tab
          // disappears on reload, so it should never look like it saved.
          set(state => {
            const { [id]: _removed, ...rest } = state.objectives;
            return { objectives: rest };
          });
          return { ok: false, error: error.message };
        }
      }
    }

    return { ok: true, id };
  },

  updateObjective: async (id, updates) => {
    const existing = get().objectives[id];
    if (!existing) return;

    const updated: ProximateObjective = {
      ...existing,
      ...(updates.parentId !== undefined && { parentId: updates.parentId ?? undefined }),
      ...(updates.orgUnitId !== undefined && { orgUnitId: updates.orgUnitId ?? undefined }),
      ...(updates.title !== undefined && { title: updates.title }),
      ...(updates.description !== undefined && { description: updates.description }),
      ...(updates.level !== undefined && { level: updates.level }),
      ...(updates.status !== undefined && { status: updates.status }),
      ...(updates.dueDate !== undefined && { dueDate: updates.dueDate ?? undefined }),
      updatedAt: new Date(),
    };

    set(state => ({ objectives: { ...state.objectives, [id]: updated } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const dbUpdates: Record<string, unknown> = { updated_at: updated.updatedAt };
        if (updates.parentId !== undefined) dbUpdates.parent_id = updates.parentId;
        if (updates.orgUnitId !== undefined) dbUpdates.org_unit_id = updates.orgUnitId;
        if (updates.title !== undefined) dbUpdates.title = updates.title;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.level !== undefined) dbUpdates.level = updates.level;
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate;

        const { error } = await supabase.from('strategy_proximate_objectives').update(dbUpdates).eq('id', id);
        if (error) console.error('[StrategyKernelStore] updateObjective error:', error);
      }
    }
  },

  deleteObjective: async (id) => {
    set(state => {
      const { [id]: _removed, ...rest } = state.objectives;
      return { objectives: rest };
    });

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_proximate_objectives').delete().eq('id', id);
        if (error) console.error('[StrategyKernelStore] deleteObjective error:', error);
      }
    }
  },

  // --------------------------------------------------------------------------
  // Take CRUD
  // --------------------------------------------------------------------------
  createTake: async (input) => {
    const id = uuidv4();
    const workspaceId = getCurrentWorkspaceId() ?? '';
    const userId = getCurrentUserId();
    const now = new Date();

    const take: StrategyTake = {
      id,
      kernelId: input.kernelId,
      proximateObjectiveId: input.proximateObjectiveId,
      workspaceId,
      orgUnitId: input.orgUnitId,
      committedActions: [],
      interdependencies: [],
      deliverables: [],
      milestoneDate: input.milestoneDate,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    set(state => ({ takes: { ...state.takes, [id]: take } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_takes').insert({
          id,
          kernel_id: take.kernelId,
          proximate_objective_id: take.proximateObjectiveId,
          workspace_id: workspaceId,
          org_unit_id: take.orgUnitId,
          committed_actions: [],
          interdependencies: [],
          deliverables: [],
          milestone_date: take.milestoneDate,
          created_by: userId,
        });
        if (error) console.error('[StrategyKernelStore] createTake error:', error);
      }
    }

    return id;
  },

  updateTake: async (id, updates) => {
    const existing = get().takes[id];
    if (!existing) return;

    // Guard: max actions per take
    if (updates.committedActions && updates.committedActions.length > MAX_TAKE_ACTIONS) {
      throw new Error(`Cannot commit more than ${MAX_TAKE_ACTIONS} actions per take.`);
    }

    const updated: StrategyTake = { ...existing, ...updates, updatedAt: new Date() };
    set(state => ({ takes: { ...state.takes, [id]: updated } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const dbUpdates: Record<string, unknown> = { updated_at: updated.updatedAt };
        if (updates.orgUnitId !== undefined) dbUpdates.org_unit_id = updates.orgUnitId;
        if (updates.committedActions !== undefined) dbUpdates.committed_actions = updates.committedActions;
        if (updates.interdependencies !== undefined) dbUpdates.interdependencies = updates.interdependencies;
        if (updates.deliverables !== undefined) dbUpdates.deliverables = updates.deliverables;
        if (updates.milestoneDate !== undefined) dbUpdates.milestone_date = updates.milestoneDate;

        const { error } = await supabase.from('strategy_takes').update(dbUpdates).eq('id', id);
        if (error) console.error('[StrategyKernelStore] updateTake error:', error);
      }
    }
  },

  deleteTake: async (id) => {
    set(state => {
      const { [id]: _removed, ...rest } = state.takes;
      return { takes: rest };
    });

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_takes').delete().eq('id', id);
        if (error) console.error('[StrategyKernelStore] deleteTake error:', error);
      }
    }
  },

  addTakeAction: async (takeId, action) => {
    const take = get().takes[takeId];
    if (!take) return;

    if (take.committedActions.length >= MAX_TAKE_ACTIONS) {
      throw new Error(`Cannot add more than ${MAX_TAKE_ACTIONS} committed actions per take.`);
    }

    const newAction: TakeAction = { id: uuidv4(), ...action };
    await get().updateTake(takeId, { committedActions: [...take.committedActions, newAction] });
  },

  addInterdependency: async (takeId, dep) => {
    const take = get().takes[takeId];
    if (!take) return;
    const newDep: TakeInterdependency = { id: uuidv4(), ...dep };
    await get().updateTake(takeId, { interdependencies: [...take.interdependencies, newDep] });
  },

  addDeliverable: async (takeId, del) => {
    const take = get().takes[takeId];
    if (!take) return;
    const newDel: TakeDeliverable = { id: uuidv4(), ...del };
    await get().updateTake(takeId, { deliverables: [...take.deliverables, newDel] });
  },

  // --------------------------------------------------------------------------
  // Assumption CRUD
  // --------------------------------------------------------------------------
  createAssumption: async (input) => {
    const id = uuidv4();
    const workspaceId = getCurrentWorkspaceId() ?? '';
    const userId = getCurrentUserId();
    const now = new Date();

    const assumption: StrategyAssumption = {
      id,
      kernelId: input.kernelId,
      workspaceId,
      assumptionText: input.assumptionText,
      reviewDate: input.reviewDate,
      status: 'active',
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    set(state => ({ assumptions: { ...state.assumptions, [id]: assumption } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_assumptions').insert({
          id,
          kernel_id: assumption.kernelId,
          workspace_id: workspaceId,
          assumption_text: assumption.assumptionText,
          review_date: assumption.reviewDate,
          status: assumption.status,
          created_by: userId,
        });
        if (error) console.error('[StrategyKernelStore] createAssumption error:', error);
      }
    }

    return id;
  },

  updateAssumption: async (id, updates) => {
    const existing = get().assumptions[id];
    if (!existing) return;

    const updated: StrategyAssumption = {
      ...existing,
      ...(updates.assumptionText !== undefined && { assumptionText: updates.assumptionText }),
      ...(updates.reviewDate !== undefined && { reviewDate: updates.reviewDate ?? undefined }),
      ...(updates.status !== undefined && { status: updates.status }),
      ...(updates.reviewNotes !== undefined && { reviewNotes: updates.reviewNotes }),
      updatedAt: new Date(),
    };

    set(state => ({ assumptions: { ...state.assumptions, [id]: updated } }));

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const dbUpdates: Record<string, unknown> = { updated_at: updated.updatedAt };
        if (updates.assumptionText !== undefined) dbUpdates.assumption_text = updates.assumptionText;
        if (updates.reviewDate !== undefined) dbUpdates.review_date = updates.reviewDate;
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.reviewNotes !== undefined) dbUpdates.review_notes = updates.reviewNotes;

        const { error } = await supabase.from('strategy_assumptions').update(dbUpdates).eq('id', id);
        if (error) console.error('[StrategyKernelStore] updateAssumption error:', error);
      }
    }
  },

  deleteAssumption: async (id) => {
    set(state => {
      const { [id]: _removed, ...rest } = state.assumptions;
      return { assumptions: rest };
    });

    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { error } = await supabase.from('strategy_assumptions').delete().eq('id', id);
        if (error) console.error('[StrategyKernelStore] deleteAssumption error:', error);
      }
    }
  },

  // --------------------------------------------------------------------------
  // Selectors
  // --------------------------------------------------------------------------
  getKernel: (id) => get().kernels[id],
  getAllKernels: () => Object.values(get().kernels),
  getChallengesForKernel: (kernelId) =>
    Object.values(get().challenges).filter(c => c.kernelId === kernelId),
  getObjectivesForKernel: (kernelId) =>
    Object.values(get().objectives).filter(o => o.kernelId === kernelId),
  getObjectivesTree: (kernelId) =>
    buildObjectivesTree(Object.values(get().objectives).filter(o => o.kernelId === kernelId)),
  getTakesForKernel: (kernelId) =>
    Object.values(get().takes).filter(t => t.kernelId === kernelId),
  getTakesForObjective: (objectiveId) =>
    Object.values(get().takes).filter(t => t.proximateObjectiveId === objectiveId),
  getAssumptionsForKernel: (kernelId) =>
    Object.values(get().assumptions).filter(a => a.kernelId === kernelId),
  getOverdueAssumptions: (kernelId) => {
    const today = new Date().toISOString().slice(0, 10);
    return Object.values(get().assumptions).filter(
      a => a.kernelId === kernelId && a.status === 'active' && a.reviewDate && a.reviewDate < today
    );
  },

  // --------------------------------------------------------------------------
  // Utility
  // --------------------------------------------------------------------------
  reset: () => set(initialState),
  setError: (error) => set({ error }),
}));
